<template>
    <div class="w-full overflow-hidden">
        <!-- Tabs -->
        <div class="flex border-b border-gray-200 dark:border-neutral-700 overflow-x-auto">
            <div v-for="(tab, index) in tabs" :key="index" @click="selectTab(index)"
                class="px-4 py-2 text-sm font-medium transition-colors border-b-2 cursor-pointer" :class="activeIndex === index
                    ? 'border-primary text-primary dark:text-primary-light'
                    : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-primary hover:border-primary/60'">
                <slot :name="'title-' + index" />
            </div>
        </div>

        <!-- Animated sliding content -->
        <div class="relative w-full overflow-hidden transition-all duration-300" :style="{ height: containerHeight + 'px' }"
            ref="container">
            <div class="flex transition-transform duration-500 ease-in-out"
                :style="{ transform: 'translateX(-' + activeIndex * 100 + '%)' }">
                <div v-for="(tab, index) in tabs" :key="index" class="w-full flex-shrink-0 px-1 h-fit" ref="tabContent">
                    <slot :name="'content-' + index" />
                </div>
            </div>
        </div>
    </div>
</template>
  
<script>
export default {
    name: "AppTabs",
    props: {
        tabsCount: { type: Number, required: true },
        value: { type: Number, default: 0 } // v-model
    },
    data() {
        return {
            activeIndex: this.value,
            tabs: Array.from({ length: this.tabsCount }, (_, i) => i),
            containerHeight: 0,
            isSliding: false
        };
    },
    mounted() {
        // Check hash on load
        const hash = window.location.hash;
        if (hash && hash.startsWith("#tab-")) {
            const index = parseInt(hash.replace("#tab-", ""));
            if (!isNaN(index) && index >= 0 && index < this.tabsCount) {
                this.activeIndex = index;
                this.$emit("step-change", index);
            }
        }

        this.$nextTick(() => this.setHeight(this.activeIndex));

        window.addEventListener("resize", this.updateHeight);
        window.addEventListener("hashchange", this.onHashChange);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.updateHeight);
        window.removeEventListener("hashchange", this.onHashChange);
    },
    watch: {
        value(val) {
            this.selectTab(val);
        }
    },
    methods: {
        selectTab(index) {
            if (index === this.activeIndex || this.isSliding) return;

            const contents = this.$refs.tabContent;
            const targetHeight = contents[index].offsetHeight;
            const currentHeight = this.containerHeight;

            // Update hash
            window.location.hash = `tab-${index}`;

            if (targetHeight > currentHeight) {
                // Taller: adjust height first
                this.containerHeight = targetHeight;
                this.slideTo(index);
            } else {
                // Shorter: slide first, then adjust
                this.slideTo(index, () => {
                    this.containerHeight = targetHeight;
                });
            }
        },
        slideTo(index, callback) {
            this.isSliding = true;
            this.activeIndex = index;

            this.$nextTick(() => {
                setTimeout(() => {
                    if (callback) callback();
                    this.isSliding = false;

                }, 500);
            });
            this.$emit("input", index);
        },
        setHeight(index) {
            const contents = this.$refs.tabContent;
            if (!contents || !contents[index]) return;
            this.containerHeight = contents[index].offsetHeight;
        },
        updateHeight() {
            this.setHeight(this.activeIndex);
        },
        recalculateHeight() {
            this.$nextTick(() => this.updateHeight());
        },
        onHashChange() {
            const hash = window.location.hash;
            if (hash && hash.startsWith("#tab-")) {
                const index = parseInt(hash.replace("#tab-", ""));
                if (!isNaN(index) && index >= 0 && index < this.tabsCount) {
                    this.selectTab(index);
                }
            }
        }
    }
};
</script>
  
<style scoped>
.flex.transition-transform {
    will-change: transform;
}
</style>
  